/**
 * Dev tracing for attachment grouping (#8070).
 *
 * Web: logs always emit in __DEV__ as `[attachment-group] …` (no URL flag required).
 * With `?attachmentScopeDebug=1`, snapshots are stored for `window.__attachmentScopeDebugDump()`.
 */

import {
  debugAttachmentScope,
  isAttachmentScopeDebugEnabled,
} from './attachmentScopeDebug';

import type { SavedAttachment } from '../services/attachmentService';
import type { GroupedAttachments } from '../types/attachmentInterfaces';
import type { AttachmentDisplayGroup } from '../utils/facilityPowerAttachmentGrouping';

type SectionAttachmentCounts = Record<string, number>;

function normalizeDebugAttachmentId(id: unknown): number | null {
  if (id == null || id === '') {
    return null;
  }
  if (typeof id === 'number' && Number.isFinite(id)) {
    return id;
  }
  const parsed = Number.parseInt(String(id), 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export function summarizeSectionAttachmentCounts(
  attachmentsBySection: Record<string, unknown> | null | undefined,
): SectionAttachmentCounts {
  const out: SectionAttachmentCounts = {};
  for (const [sectionId, raw] of Object.entries(attachmentsBySection ?? {})) {
    if (Array.isArray(raw)) {
      out[sectionId] = raw.filter(item => item != null).length;
    } else if (raw != null) {
      out[sectionId] = 1;
    }
  }
  return out;
}

export function summarizeAttachmentIdList(
  attachments:
    | ReadonlyArray<SavedAttachment | null | undefined>
    | null
    | undefined,
): Array<number | string | null> {
  return (attachments ?? []).map(attachment => {
    if (attachment == null) {
      return null;
    }
    const normalized = normalizeDebugAttachmentId(attachment.id);
    return normalized ?? attachment.id ?? null;
  });
}

export function summarizeVisibleAttachmentItems(
  items: ReadonlyArray<{
    sectionId: string;
    attachment: SavedAttachment;
  }>,
): Array<{
  sectionId: string;
  id: number | string | null;
  requestSection: string | undefined;
}> {
  return items.map(item => ({
    sectionId: item.sectionId,
    id:
      normalizeDebugAttachmentId(item.attachment.id) ??
      item.attachment.id ??
      null,
    requestSection: item.attachment.requestSection,
  }));
}

export function summarizeDisplayGroups(
  groups:
    | ReadonlyArray<AttachmentDisplayGroup | null | undefined>
    | null
    | undefined,
): {
  groupCount: number;
  itemCount: number;
  groups: Array<{
    groupKey: string;
    label: string;
    itemCount: number;
    sectionIds: string[];
    attachmentIds: Array<number | string | null>;
  }>;
} {
  const safeGroups = (groups ?? []).filter(
    (group): group is AttachmentDisplayGroup => group != null,
  );
  const summarized = safeGroups.map(group => ({
    groupKey: group.groupKey,
    label: group.label,
    itemCount: group.items?.length ?? 0,
    sectionIds: [...new Set((group.items ?? []).map(item => item.sectionId))],
    attachmentIds: summarizeAttachmentIdList(
      (group.items ?? []).map(item => item.attachment),
    ),
  }));
  return {
    groupCount: summarized.length,
    itemCount: summarized.reduce((sum, group) => sum + group.itemCount, 0),
    groups: summarized,
  };
}

export function summarizeGroupedAttachments(
  groups:
    | ReadonlyArray<GroupedAttachments | null | undefined>
    | null
    | undefined,
): {
  groupCount: number;
  substageCount: number;
  attachmentCount: number;
  groups: Array<{
    stage: string;
    stageLabel: string;
    substageCount: number;
    attachmentCount: number;
    attachmentIds: Array<number | string | null>;
  }>;
} {
  const safeGroups = (groups ?? []).filter(
    (group): group is GroupedAttachments => group != null,
  );
  const summarized = safeGroups.map(group => {
    const substages = group.substages ?? [];
    const attachments = substages.flatMap(
      substage => substage.attachments ?? [],
    );
    return {
      stage: group.stage,
      stageLabel: group.stageLabel,
      substageCount: substages.length,
      attachmentCount: attachments.length,
      attachmentIds: summarizeAttachmentIdList(attachments),
    };
  });
  return {
    groupCount: summarized.length,
    substageCount: summarized.reduce(
      (sum, group) => sum + group.substageCount,
      0,
    ),
    attachmentCount: summarized.reduce(
      (sum, group) => sum + group.attachmentCount,
      0,
    ),
    groups: summarized,
  };
}

/** Always warns in dev web; stores snapshot when `?attachmentScopeDebug=1`. */
export function debugAttachmentGrouping(
  step: string,
  data: Record<string, unknown>,
): void {
  if (typeof __DEV__ === 'undefined' || !__DEV__) {
    return;
  }
  if (typeof window !== 'undefined') {
    console.warn(`[attachment-group] ${step}`, data);
  }
  debugAttachmentScope(`group.${step}`, data);
}

export function debugAttachmentGroupingPath(
  path: string,
  input: Record<string, unknown>,
  output: Record<string, unknown>,
  chosen: boolean,
): void {
  debugAttachmentGrouping(`path.${path}`, {
    chosen,
    input,
    output,
  });
}

declare global {
  interface Window {
    __attachmentGroupingDebugHelp?: () => void;
  }
}

export function registerAttachmentGroupingDebugGlobals(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.__attachmentGroupingDebugHelp = () => {
    console.info(
      '[attachment-group] Dev logs: watch console for [attachment-group] …',
    );
    console.info(
      '[attachment-group] Enable snapshot storage with ?attachmentScopeDebug=1 then call window.__attachmentScopeDebugDump()',
    );
    console.info(
      `[attachment-group] Snapshot storage active: ${String(
        isAttachmentScopeDebugEnabled(),
      )}`,
    );
  };
}
